import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, NavigationExtras, ParamMap } from '@angular/router';
import { state2, cacheTimeMax, IStateOptions, IHistoryItem, IStateOptions2 } from './state';
import { filter } from 'rxjs/operators';
import { Location } from '@angular/common';
import { getSearchArgFromRoute, ICommonSearchArgs, ICommonSearchRouteArgs } from 'src/app/shared/common-search-args';


export interface INotitiesSearchOptions {
  allowFromCache: boolean;
  forceRefresh: boolean;
  addToHistory: boolean;
}

function emptyToNull(value: string) { return (value === '' || value === 'null' || value === undefined) ? null : value; }

/**
 * AppRoutingService provides routing services
 * @usageNotes
 * ```
 * - in een component roep createCurrentHistoryItem aan vanuit component constructor (dit zal huidige routing url registreren als currentHistoryItem)
 * - standaard geef je hier geen url mee omdat deze ingevuld wordt door appRoutingService NavigationEnd subscribe, welke de juiste url zet voor currentHistoryItem
 * - geef wel de url parameter mee wanneer je wil navigeren naar een andere component dan de huidige component met de backbutton.
 *
 * navigateTo : gebruik in de huidige component om te navigeren naar een andere component waarbij de backbutton verwijst naar de huidige component.
 *              navigateTo zal automatisch de currentHistoryKey toevoegen aan queryParams zodat de volgende component weet naar welk historyItem de backbutton moet verwijzen.
 * navigateToReplaceHistory : gebruik in de huidige component om te navigeren naar dezelfde component zonder de history te wijzigen. Daardoor blijft de backbutton ongewijzigd.
 *                            gebruikt bij navigatie naar dezelfde component maar gewijzigd compoent state (b.v. filter toepassen)
 * navigateBack : navigeert naar de vorige pagina  (werkt enkel wanneer je naar de huidige component navigeert met navigateTo of !!)
 *
 * gebruik appService.routing.backHistoryItem.title om te binden met de backutton en roep appService.routing.navigateBack aan op klik event
 * toon backbutton enkel wanneer appService.routing.backHistoryItem een waarde heeft gebruik makend van *ngIf
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AppRoutingService {
  public currentHistoryKey: string;
  public currentHistoryItem: IHistoryItem;
  backHistoryKey: string;
  // channel: BroadcastChannel;
  constructor(private router: Router, private route: ActivatedRoute, private location: Location) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      const historyKey = this.route.snapshot.queryParamMap.get('h');
      this.setBackHistoryItem(historyKey);
      this.updateCurrentHistory(event.url);
    });
  }

  // setDebugChannel(channel: BroadcastChannel) {
  //   this.channel = channel;
  // }

  /**
   * creëert currentHistoryItem (aan te roepen vanuit constructor van een component)
   * navigationEnd subscription in deze service zal automatisch de juist url invullen in currentHistoryItem
   */
  public createCurrentHistoryItem(title: string, url: string = null) {
    this.currentHistoryKey = '_' + Math.random().toString(36).substr(2, 9);
    this.currentHistoryItem = { timestamp: new Date(), title, url, doNotReplace: url != null };
    // this.channel.postMessage({ type: 'createCurrent', message: `key:${this.currentHistoryKey}, title:${title}` });
    // this.channel.postMessage({ type: 'routing', historyItem: this.currentHistoryItem, key: this.currentHistoryKey, keytype: 'current' });
    state2.addToHistory(this.currentHistoryKey, this.currentHistoryItem);
  }

  public createCurrentHistoryItemFromRoute(title: string, commands: any[], extras?: NavigationExtras) {
    const urlTree = this.router.createUrlTree(commands, extras);
    const url = urlTree.toString();
    this.currentHistoryKey = '_' + Math.random().toString(36).substr(2, 9);
    this.currentHistoryItem = { timestamp: new Date(), title, url };
    // this.channel.postMessage({ type: 'routing', historyItem: this.currentHistoryItem, key: this.currentHistoryKey, keytype: 'current' });
    state2.addToHistory(this.currentHistoryKey, this.currentHistoryItem);
  }

  private updateCurrentHistory(url: string) {
    if (!this.currentHistoryItem || this.currentHistoryItem.doNotReplace) { return; }
    this.currentHistoryItem.timestamp = new Date();
    this.currentHistoryItem.url = url;
    // this.channel.postMessage({ type: 'routing', historyItem: this.currentHistoryItem, key: this.currentHistoryKey, keytype: 'current' });
    // this.channel.postMessage({ type: 'updateCurrent', message: `key:${this.currentHistoryKey}=${this.currentHistoryItem.url}` });
  }

  public backHistoryItem: IHistoryItem;
  private setBackHistoryItem(itemKey: string) {
    this.backHistoryKey = itemKey;
    this.backHistoryItem = state2.getHistoryItem(itemKey);
    // this.channel.postMessage({ type: 'routing', historyItem: this.backHistoryItem, key: this.backHistoryKey, keytype: 'back' });
    // this.channel.postMessage({ type: 'restoreBackitemFromQueryParam', message: `key:${itemKey}=${(this.backHistoryItem) ? this.backHistoryItem.url : '-- notfound--'}` });
  }

  stateOptions: IStateOptions2;
  public setBackStateOptions(stateOptions: IStateOptions2) {
    this.stateOptions = stateOptions;
  }

  public paramMapToObject<Tobject>(paramMap: ParamMap, initial: Tobject) {
    return paramMap.keys.reduce((obj, key) => { obj[key] = paramMap.get(key); return obj; }, initial);
  }

  public paramMapToSearchArgs(paramMap: ParamMap, initial: ICommonSearchArgs) {
    const searchArgFromParams = this.paramMapToObject(paramMap, {} as ICommonSearchRouteArgs);
    const searchArg = getSearchArgFromRoute(searchArgFromParams);
    return { ...initial, ...searchArg } as ICommonSearchArgs;
  }

  /**
   * Navigates back to the previous component set up by createHistoryItem in the constructor of the previous component
   */
  public navigateBack() {
    // console.log(`navigate back to ${this.backHistoryItem.url}`);
    if (this.backHistoryItem.url==null){
      this.router.navigateByUrl("/dashboard",{state:this.stateOptions});
      return;
    }
    if (this.backHistoryItem.url.startsWith('/#')){
      this.backHistoryItem.url = this.backHistoryItem.url.replace('/#','/dashboard#');
    }
    this.router.navigateByUrl(this.backHistoryItem.url, { state: this.stateOptions });
  }
  // getHistoryKey() { return this.currentHistoryKey; }
  public navigateTo(commands: any[], extras?: NavigationExtras) {
    //console.log(`navigateto ${this.currentHistoryKey}`);
    const newExtras = extras || {};
    newExtras.queryParams = { ...newExtras.queryParams, ...{ h: this.currentHistoryKey } };
    this.router.navigate(commands, newExtras);
  }

  public setCurrentTitle(title: string) {
    this.currentHistoryItem.title = title;
  }

  /**
   * Navigeer zonder item toe te voegen aan geschiedenis
   */
  public navigateToReplaceHistory(title: string, commands: any[], extras?: NavigationExtras) {
    const newExtras = extras || {};
    newExtras.queryParams = { ...newExtras.queryParams, ...{ h: this.backHistoryKey } };
    this.currentHistoryItem.title = title;
    this.router.navigate(commands, newExtras);
  }

  /**
   * Roep setFragment methode in an component aan als je naar een speficieke plaats in de pagina wil terugkeren met de navigateBack (backbutton)
   * dit veronderstelt dat je in je component html attribute id voorziet om de plaats te markeren naar waar je wil springen in de pagina
   */
  public setFragment(fragment: string) {
    const urlTree = this.router.parseUrl(this.currentHistoryItem.url);
    urlTree.fragment = fragment;
    this.currentHistoryItem.url = this.router.serializeUrl(urlTree);
  }

  public replaceUrlWithoutNavigating(url: string) { this.location.go(url); }
  public replaceQueryParameter(name: string, value: string) {
    const currentPath = this.location.path();
    const pattern = new RegExp(';CurrentPage=\\d+', 'gi');
    this.location.go(currentPath.replace(pattern, `;${name}=${value}`));
  }

  public createNavigateToDetailZittingHistoryItem(zittingId: number) {
    const bespreken = false;
    this.createCurrentHistoryItemFromRoute('Detail zitting', ['/detailzitting', zittingId, { viewstatename:bespreken ? 'bespreken' : 'nietbespreken' }]);
  }

  /**
   * navigeer naar detail van zitting en voeg historyItem van huidige component toe aan history
   */
  public navigateToDetailZitting(zittingId: number, bespreken: boolean = false, docId?: number) {
    if (docId) {
      this.navigateTo(['/detailzitting', zittingId, docId, { viewstatename:bespreken ? 'bespreken' : 'nietbespreken' }]);
    } else {
      this.navigateTo(['/detailzitting', zittingId, { viewstatename:bespreken ? 'bespreken' : 'nietbespreken' }]);
    }
  }
  /**
   * navigeer naar detail zititng zonder de historiek te wijzigen (enkel gebruiken vanuit detail zitting)
   */
  public navigateToDetailZittingKeepHistory(zittingId: number, viewstatename:string='nietbespreken', docId?: number) {
    if (docId) {
      this.navigateToReplaceHistory('Naar de zitting', ['/detailzitting', zittingId, docId, { viewstatename }]);
    } else {
      this.navigateToReplaceHistory('Naar de zitting', ['/detailzitting', zittingId, { viewstatename }]);
    }
  }

  public navigateToZoekAgendapunten(searchArg: ICommonSearchArgs | string, searchOptions: IStateOptions2 = { allowFromCache: false, concat: false, page: 0 }) {
    const newSearchOptions = { ... { cacheTime: cacheTimeMax }, ...searchOptions };
    const newSearchArg: ICommonSearchArgs = (typeof searchArg === 'string')
      ? { CurrentPage: 0, SearchFor: searchArg, CombineWordsWith: ' or ' }
      : searchArg;
    this.router.navigate(['/zoek', newSearchArg], { replaceUrl: !searchOptions.addToHistory, state: newSearchOptions });
  }

  // public navigateToZoekAgendapunten2(searchArg: ICommonSearchArgs | string, zoekState: IZoekState) {
  //   const newSearchArg: ICommonSearchArgs = (typeof searchArg === 'string')
  //     ? { CurrentPage: 0, SearchFor: searchArg, CombineWordsWith: ' or ' }
  //     : searchArg;
  //   this.setZoekState(zoekState);
  //   this.router.navigate(['/zoek', newSearchArg], { replaceUrl: !zoekState.addToHistory });
  // }

  /**
   * stateOptions default {concat:false, allowFromCache:false, page:0}
   */
  public navigateToZoekNotities(searchArg: ICommonSearchArgs | string, stateOptions: IStateOptions2 = { concat: false, allowFromCache: false, page: 0 }) {
    const newSearchArg: ICommonSearchArgs = (typeof searchArg === 'string')
      ? { SearchFor: searchArg, SoortNotities: 'alle', CurrentPage: 0, CombineWordsWith: ' or ', PeriodeFilter: 'y' }
      : searchArg;
    // const extras = { replaceUrl: !addToHistory };
    stateOptions.pageSize = 5;
    this.router.navigate(['notities', 'zoeken', newSearchArg], { state: stateOptions });
  }


  public navigateToZoekDocumentenZitting(searchArg: ICommonSearchArgs | string, cache = false) {
    const options: IStateOptions = { allowFromCache: cache, concat: cache, cacheTime: cacheTimeMax };
    state2.set('documentzittingsearchoptions', options);
    const newSearchArg: ICommonSearchArgs = (typeof searchArg === 'string')
      ? { SearchFor: searchArg, SoortZittingId: null, CurrentPage: 0, CombineWordsWith: ' or ', PeriodeFilter: 'y' }
      : searchArg;
    this.router.navigate(['/zoekdocumentzitting', newSearchArg]);
  }

  public navigateToDetailPunt(zittingId: number, puntId: number, bespreken: boolean = false, fragment: string = null) {
    let extras: NavigationExtras;
    if (fragment) {
      extras = { fragment };
    }
    this.navigateTo(['/agendapunt', zittingId, puntId, bespreken], extras);
  }
  public navigateToDetailPuntReplaceHistory(backButtonTitel: string, zittingId: number, puntId: number, bespreken: boolean = false, fragment: string = null) {
    this.navigateToReplaceHistory(backButtonTitel, ['/agendapunt', zittingId, puntId, bespreken]);
  }

  public navgateToWijzigNotitie(zittingId: number, puntId: number, notitieId: number) {
    this.navigateTo(['/notities/wijzig', zittingId, puntId, notitieId]);
  }
  public navigateToReageerNotitie(zittingId: number, puntId: number, notitieId: number) {
    this.navigateTo(['/notities/reageer', zittingId, puntId, notitieId]);
  }
  public navigateToNotitieToevoegen(zittingId: number, puntId: number) {
    this.navigateTo(['/notities/toevoegen', zittingId, puntId]);
  }

  public navigateToBoaDetail(id: string) {
    this.navigateTo(['/agendapunt/boadetail', id]);
  }


}
